import { Button, Grid, GridItem, Message, ResourceContext } from '@flos/react-ui';
import { FormConfig } from '@topdanmark-onlinesolutions/claim-form-types';
import { FC, useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfigContext } from '../context/ConfigContext';
import { SubmitContext } from '../context/SubmitContext';
import { getPageInfo } from '../helpers/getPageInfo';
import { hasSkippedPages } from '../helpers/hasSkippedPages';
import { PagesAndFields, areAllPagesValid, getPageAndFieldValidity, isThisPageValid } from '../helpers/pageValidity';
import { onSubmit } from '../helpers/submit';
import useAppState from '../state/appState';
import { trackSubmitAfterResumeFromSave, trackTimeSpentOnPage, trackUserClickedBack } from '../utils/tracking';
import { trackInfo } from '../track-by-submit/common/tracking';
import { trackInfoEventValue } from '../utils/trackingWrapper';
import NextButton from './navigation/NextButton';
import PreviousButton from './navigation/PreviousButton';
import ResumeButton from './navigation/ResumeButton';
import SubmitButton from './navigation/SubmitButton';
import SaveIndicator from './SaveIndicator';
import { scrollPageToTop } from '../helpers/scrollPageToTop';
import { getValue } from './values/Get';
import { CarbucksService, UpdateReservationParams } from '../services/carBucks';

type FormNavigationProps = {
    pageId: string;
};

const FormNavigation: FC<FormNavigationProps> = ({ pageId }) => {
    const formConfig = useAppState((state) => state.formConfig);
    const formValues = useAppState((state) => state.formValues);
    const formStartTime = useAppState((state) => state.formStartTime);
    const resumeFromSave = useAppState((state) => state.resumeFromSave);
    const hasReachedSummary = useAppState((state) => state.hasReachedSummary);
    const claimNumber = useAppState((state) => state.claimNumber);
    const resetForm = useAppState((state) => state.resetForm);
    const setClaimFormPayload = useAppState((state) => state.setClaimFormPayload);

    const error = useAppState((state) => state.error);
    const setError = useAppState((state) => state.setError);

    const navigate = useNavigate();

    const labels = useContext(ResourceContext);

    const { isSubmitting, setIsSubmitting, setIsRedirecting, isRedirecting } = useContext(SubmitContext);
    const appConfig = useContext(ConfigContext);

    const [pageIdForTimer, setPageIdForTimer] = useState<string>('');
    const [pageTimer, setPageTimer] = useState<number | undefined>(undefined);

    const [pageValid, setPageValid] = useState(false);
    const [showResumeBtn, setShowResumeBtn] = useState(false);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [showSaveIndicator, setShowSaveIndicator] = useState(false);
    const [loading, setLoading] = useState({
        next: false,
        previous: false
    });

    // Carbucks tracking - big/small
    useEffect(() => {
        const handleBeforeUnload = () => {
            if (pageId === 'hvilken-skade' && formConfig) {
                trackInfo(formConfig, [{ key: 'left-hvilken-skade', value: true }]);
            }
        };
        window.addEventListener('beforeunload', handleBeforeUnload);
        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
        };
    }, [pageId, formConfig]);

    const resetError = () => {
        setError('');
    };

    const trackTimeSpent = (
        pageId: string,
        pageIdForTimer: string,
        pageTimer: number | undefined,
        formConfig: FormConfig | undefined
    ) => {
        if (!formConfig) {
            return;
        }
        /**
         * If pageIdForTimer <> pageId then check pageIdForTimer and pageTimer is defined
         * set timeSpentOnPage for the previous page the user was on. ELSE start the timer for the page
         * and store in state. Track the time in Kibana. If pageId is empty it is the end of the form (submit)
         */
        if (pageId === pageIdForTimer) {
            setPageTimer(Math.round(Date.now() / 1000));
        } else {
            if (pageIdForTimer && pageTimer) {
                const usageTime = Math.round(Date.now() / 1000) - pageTimer;
                trackTimeSpentOnPage(formConfig, pageIdForTimer, usageTime);
            }
            setPageIdForTimer(pageId);
        }
    };

    const shouldShowResumeButton = async (pagesAndFieldsValidity: PagesAndFields[] | undefined) => {
        if (!formConfig) {
            return;
        }

        const _pagesAndFieldsValidity =
            pagesAndFieldsValidity ?? (await getPageAndFieldValidity(formConfig, formValues));
        const allPagesAreValid = areAllPagesValid(_pagesAndFieldsValidity);
        const pageNumber = _pagesAndFieldsValidity.findIndex((page) => page.pageId === pageId) + 1;
        const isSecondLastPage = pageNumber + 1 === _pagesAndFieldsValidity.length ? true : false;
        const showButtonResume = enableBackToResumeButton && allPagesAreValid && hasNextPage && !isSecondLastPage;
        setShowResumeBtn(showButtonResume);
    };

    useEffect(() => {
        if (!formConfig) {
            return;
        }

        setPageValid(isThisPageValid(formConfig, formValues, pageId));
        if (hasReachedSummary) {
            // the resume to summary page button needs to be recalculated
            // this recalc will call the api on EVERY keystroke as formValues are updated
            // debounce this to save some calls
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            debounceRef.current = setTimeout(() => shouldShowResumeButton(undefined), 1000); // wait abit on each input before recalculating
        }
        setShowSaveIndicator(false);
        // eslint-disable-next-line
    }, [formConfig, formValues, pageId]);

    useEffect(() => {
        trackTimeSpent(pageId, pageIdForTimer, pageTimer, formConfig);
    }, [pageId, pageIdForTimer, pageTimer, formConfig]);

    if (!formConfig) {
        return null;
    }

    const { hasNextPage, isFirstPage, isLastPage, urlResume } = getPageInfo(formConfig, formValues, pageId, resetForm);
    const { enableBackToResumeButton } = formConfig;
    const { pageType } = formConfig.pages.find((page) => page.pageId === pageId) ?? { pageType: '' };

    // Get current page URL and remove hash
    const curPageUrl = new URL(window.location.href);
    curPageUrl.hash = '';

    const trackExpiredSessionUserClick = () => {
        trackInfoEventValue(formConfig, 'session-expired-by-submit-user-clicked', true, '');
    };

    const trackUserClickedOnContactByError = () => {
        trackInfoEventValue(formConfig, 'tech-error-user-clicked-to-contact', true, '');
    };

    const handleLoading = (btn: 'next' | 'previous', loading: boolean) => {
        setLoading((cur) => ({
            ...cur,
            [btn]: loading
        }));
    };

    const handleNextClick = async () => {
        try {
            handleLoading('next', true);
            const pagesAndFieldsValidity = await getPageAndFieldValidity(formConfig, formValues);
            const thisPageIndex = pagesAndFieldsValidity.findIndex((pages) => pages.pageId === pageId);
            const nextPage = pagesAndFieldsValidity[thisPageIndex + 1]?.pageId ?? undefined;
            const invalidPageId = await hasSkippedPages(formConfig, formValues, pageId, pagesAndFieldsValidity);
            // if there is a skipped page/invalid page before this, navigate to it, otherwise navigate to the next page
            const nextUrl =
                invalidPageId !== false
                    ? `/${formConfig.formId}/${invalidPageId}`
                    : nextPage !== undefined
                    ? `/${formConfig.formId}/${nextPage}`
                    : undefined;
            handleLoading('next', false);
            if (nextUrl) {
                if (formConfig.enableSave && !isFirstPage && !isLastPage && enableBackToResumeButton) {
                    setShowSaveIndicator(true);
                    setTimeout(() => {
                        navigate(nextUrl);
                        scrollPageToTop();
                    }, 500);
                } else {
                    navigate(nextUrl);
                    scrollPageToTop();
                }

                resetError();
            } else {
                return;
            }
        } catch (error) {
            console.error('Error in handleNextClick', error);
            setError('handleNextClick');
        }
    };

    const handlePreviousClick = async () => {
        try {
            handleLoading('previous', true);
            const pagesAndFieldsValidity = await getPageAndFieldValidity(formConfig, formValues);
            const thisPageIndex = pagesAndFieldsValidity.findIndex((pages) => pages.pageId === pageId);
            let prevPage = pagesAndFieldsValidity[thisPageIndex - 1]?.pageId ?? undefined;
            /*Carbucks experiment*/
            if (formConfig.formId === 'parkeringsskade-privat' && pageId === 'godt-at-vide') {
                prevPage = 'hvilken-skade';
            }

            handleLoading('previous', false);
            if (prevPage) {
                navigate(`/${formConfig.formId}/${prevPage}`);
                resetError();

                trackUserClickedBack(formConfig, pageId);
            } else {
                return;
            }
        } catch (error) {
            console.error('Error in handlePreviousClick', error);
            setError('handlePreviousClick');
        }
    };

    const handleSubmitClick = async () => {
        try {
            const invalidPageId = await hasSkippedPages(formConfig, formValues, pageId);
            if (invalidPageId !== false) {
                navigate(`/${formConfig.formId}/${invalidPageId}`);
                resetError();
            } else {
                // We only want to check Carbucks reservations if both pages are present:
                // - "find-vaerksted" contains the supManWidget element
                // - "hvilken-skade"
                const requiredIds = ['hvilken-skade', 'find-vaerksted'];
                const hasBothPages = requiredIds.every((id) => formConfig.pages.some((page) => page.pageId === id));

                if (hasBothPages) {
                    // if this claim has a Carbucks reservation, make sure it is still valid
                    const formValue = getValue(formConfig, formValues, 'supManWidget');
                    const damageSizeValue = getValue(formConfig, formValues, 'damageSize');

                    const hasCarbucksReservation = formValue && formValue.carbucksReservation;
                    const hadCarbucksOption = formValue && formValue.carbucksWorkshops > 0;

                    if (hadCarbucksOption) {
                        if (hasCarbucksReservation) {
                            const updateParams: UpdateReservationParams = {
                                reservationGUID: formValue.carbucksReservation.reservationGUID,
                                orderGUID: formValue.carbucksReservation.orderGUID,
                                carbucksGUID: formValue.carbucksReservation.carbucksGUID,
                                // the reservationStartDatetime/reservationEndDatetime has format '2025-09-08 07:30:00.000000',
                                // so we need to strip the ending '.000000' part
                                slotStartDateTime: formValue.carbucksReservation.reservationStartDatetime.split('.')[0],
                                slotEndDateTime: formValue.carbucksReservation.reservationEndDatetime.split('.')[0]
                            };

                            const eventKey =
                                damageSizeValue.value === 'small'
                                    ? 'carbucks-appointment-booked-small'
                                    : damageSizeValue.value === 'big'
                                    ? 'carbucks-appointment-booked-big'
                                    : 'carbucks-appointment-booked-unknown';

                            const isReservationStillValid = await CarbucksService.validateReservation(
                                appConfig,
                                updateParams
                            );

                            if (!isReservationStillValid) {
                                // Track failed/invalid Carbucks reservation
                                trackInfo(formConfig, [
                                    { key: 'carbucks-appointment-invalid-reservation', value: true }
                                ]);
                                setError('carbucksReservationInvalid');
                                return;
                            }

                            trackInfo(formConfig, [{ key: eventKey, value: true }]);
                            trackInfo(formConfig, [{ key: 'carbucks-appointment-booked', value: true }]);
                            trackInfo(formConfig, [
                                {
                                    key: 'carbucks-appointment-submitted',
                                    value: formValue.carbucksReservation.reservationGUID
                                }
                            ]);
                        } else {
                            damageSizeValue &&
                                trackInfo(formConfig, [{ key: 'carbucks-appointment-skipped', value: true }]);
                        }
                    } else {
                        // They had a Carbucks option but continued without booking
                        damageSizeValue &&
                            trackInfo(formConfig, [{ key: 'carbucks-appointment-skipped', value: true }]);
                    }
                }

                trackTimeSpent('', pageIdForTimer, pageTimer, formConfig);
                trackSubmitAfterResumeFromSave(formConfig, resumeFromSave);

                onSubmit(
                    formConfig,
                    formValues,
                    appConfig,
                    formStartTime,
                    claimNumber,
                    labels(`response.time.${formConfig?.automation.receiptEmail}`),
                    resetError,
                    setError,
                    resetForm,
                    setIsSubmitting,
                    setIsRedirecting,
                    setClaimFormPayload
                );
            }
        } catch (error) {
            console.error('Error in handleSubmitClick', error);
            setError('handleSubmitClick');
        }
    };

    const makeNewCarbucksReservation = () => {
        navigate(`/${formConfig.formId}/find-vaerksted`);
        resetError();
    };

    return (
        <div key={pageId}>
            {error !== '' && (
                <div className="u-spacing-stack-m">
                    <Message theme="error" title={<h4>Der opstod en fejl</h4>}>
                        {(() => {
                            switch (error) {
                                case 'SESSION-EXPIRED':
                                    return (
                                        <div>
                                            <p>Sessionen er udløbet. Log ind igen for at fortsætte.</p>
                                            <p>
                                                <a
                                                    className="Go"
                                                    href={curPageUrl.toString()}
                                                    onClick={trackExpiredSessionUserClick}
                                                >
                                                    &nbsp;Log ind i Mit Topdanmark
                                                </a>
                                            </p>
                                        </div>
                                    );
                                case 'carbucksReservationInvalid':
                                    return (
                                        <>
                                            <p>
                                                Den valgte værkstedstid er desværre ikke længere tilgængelig. Vælg
                                                venligst en ny tid for at fortsætte.
                                            </p>

                                            <Button theme="ghost" onClick={makeNewCarbucksReservation}>
                                                Vælg ny værkstedstid
                                            </Button>
                                        </>
                                    );
                                default:
                                    return (
                                        <p>
                                            Prøv at indsende anmeldelsen igen. Hvis du fortsat ikke kan indsende den, er
                                            du velkommen til at{' '}
                                            <a href="/kontakt" onClick={trackUserClickedOnContactByError}>
                                                kontakte os.
                                            </a>
                                        </p>
                                    );
                            }
                        })()}
                    </Message>
                </div>
            )}
            {isRedirecting ? (
                <></>
            ) : (
                <>
                    {error !== 'SESSION-EXPIRED' && (
                        <Grid gap="none" className="button-row">
                            <GridItem span={6}>
                                {!isLastPage ? (
                                    <div className="button-wrapper">
                                        <NextButton
                                            isPageValid={pageValid}
                                            isLoading={loading.next}
                                            onClick={handleNextClick}
                                        />
                                        {!isFirstPage && pageType !== 'landing-page' && showSaveIndicator && (
                                            <SaveIndicator />
                                        )}
                                    </div>
                                ) : (
                                    <SubmitButton submitIsLoading={isSubmitting} onClick={handleSubmitClick} />
                                )}

                                {!isFirstPage && (
                                    <PreviousButton
                                        isLoading={loading.previous}
                                        submitIsLoading={isSubmitting}
                                        onClick={handlePreviousClick}
                                    />
                                )}
                            </GridItem>

                            <GridItem span={6} className="u-text-right">
                                {showResumeBtn && (
                                    <ResumeButton
                                        pageId={pageId}
                                        submitIsLoading={isSubmitting}
                                        formConfig={formConfig}
                                        urlResume={urlResume}
                                        resetError={resetError}
                                    />
                                )}
                            </GridItem>
                        </Grid>
                    )}
                </>
            )}
        </div>
    );
};

export default FormNavigation;
